import React, { useContext } from 'react';
import { logError } from './sentry';

type Props = {
  fallback: React.ReactNode;
  children: React.ReactNode;
  logger?: (e: Error) => void;
};

type State =
  | {
      error: undefined;
      hasError: false;
    }
  | {
      error: Error;
      hasError: true;
    };

const Context = React.createContext([new Error(''), () => {}] as const);

export function useError() {
  return useContext(Context);
}

export class ErrorBoundary extends React.Component<Props, State> {
  static getDerivedStateFromError(error: Error) {
    return { error, hasError: true };
  }

  state: State = {
    error: undefined,
    hasError: false,
  };

  componentDidCatch(error: Error) {
    const { logger = logError } = this.props;
    logger(error);
  }

  clearError = () => {
    this.setState({
      error: undefined,
      hasError: false,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Context.Provider value={[this.state.error, this.clearError]}>
          {this.props.fallback}
        </Context.Provider>
      );
    }

    return this.props.children;
  }
}
